import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  MessageSquare,
  AtSign,
  Users,
  BookOpen,
  MessageCircle,
  Star,
  Briefcase,
  Info,
  CheckCheck,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNotificationsStore } from '../stores/notifications-store';
import type { AppNotification, NotificationType } from '../lib/api';
import { EmptyState } from './ui/empty-state';
import { Rise } from './ui/rise';

// ─── Icon map ─────────────────────────────────────────────────────────────────

const TYPE_ICONS: Record<NotificationType, typeof Bell> = {
  message: MessageSquare,
  mention: AtSign,
  collaboration_invite: Users,
  project_shared: BookOpen,
  comment: MessageCircle,
  review: Star,
  hire_request: Briefcase,
  system: Info,
};

// ─── Accent map — a=âmbar (conquistas/oportunidades), b=aurora (social/mensagens),
//     c=azul (sistema), espelhando o contrato visual de #scr-notificacoes ──────

const TYPE_ACCENT: Record<NotificationType, 'a' | 'b' | 'c'> = {
  review: 'a',
  hire_request: 'a',
  message: 'b',
  mention: 'b',
  comment: 'b',
  collaboration_invite: 'b',
  project_shared: 'b',
  system: 'c',
};

// ─── Relative time ────────────────────────────────────────────────────────────

function relativeTime(dateStr: string, t: (key: string, defaultValue: string) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t('notificationsView.now', 'agora');
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: d > 365 ? 'numeric' : undefined,
  });
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

// ─── Navigation helper ────────────────────────────────────────────────────────

function getNotificationUrl(notification: AppNotification): string | null {
  const { data } = notification;
  if (data.conversationId) return `/app/messages?c=${data.conversationId}`;
  if (data.projectId) return `/app/editor/${data.projectId}`;
  if (data.url) return data.url as string;
  return null;
}

// ─── Single row — padrão .ntf do contrato visual ─────────────────────────────

function NotificationRow({ notification }: { notification: AppNotification }) {
  const { t } = useTranslation();
  const { markRead } = useNotificationsStore();
  const navigate = useNavigate();
  const Icon = TYPE_ICONS[notification.type] ?? Bell;
  const accent = TYPE_ACCENT[notification.type] ?? 'c';

  const handleClick = async () => {
    if (!notification.read) {
      await markRead(notification.id);
    }
    const url = getNotificationUrl(notification);
    if (url) navigate(url);
  };

  return (
    <button
      onClick={handleClick}
      className={cn('ntf w-full text-left', !notification.read && 'unread')}
    >
      <i className={accent}>
        <Icon size={15} aria-hidden />
      </i>
      <span className="flex-1 min-w-0">
        <b>{notification.title}</b>
        {notification.body && (
          <small className="line-clamp-3">{notification.body}</small>
        )}
      </span>
      <span className="tm">{relativeTime(notification.createdAt, t)}</span>
    </button>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

// ─── Main view ────────────────────────────────────────────────────────────────

export function NotificationsView() {
  const { t } = useTranslation();
  const { notifications, unreadCount, isLoading, fetchNotifications, markAllRead } =
    useNotificationsStore();
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const todayItems = notifications.filter((n) => isToday(n.createdAt));
  const olderItems = notifications.filter((n) => !isToday(n.createdAt));

  // Paginate older items
  const paginatedOlder = olderItems.slice(0, page * PAGE_SIZE);
  const hasMore = paginatedOlder.length < olderItems.length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header — "O que a noite trouxe" (contrato visual #scr-notificacoes) */}
      <div className="shrink-0 px-4 pt-4 pb-4 md:px-8 md:pt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <p className="m-eyebrow">{t('notificationsView.title', 'Notificações')}</p>
          <h1 className="font-serif text-[22px] font-medium leading-snug text-foreground mt-1.5 truncate">
            {unreadCount > 0
              ? unreadCount === 1
                ? t('notificationsView.subtitleUnreadSingular', '{{count}} não lida', { count: unreadCount })
                : t('notificationsView.subtitleUnreadPlural', '{{count}} não lidas', { count: unreadCount })
              : t('notificationsView.allCaughtUp', 'Tudo em dia')}
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {unreadCount > 0 && (
            <button
              onClick={() => markAllRead()}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-primary border border-primary/20 hover:bg-primary/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CheckCheck size={13} />
              {t('notificationsView.markAll', 'Marcar todas')}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto [-webkit-overflow-scrolling:touch]">
        {isLoading && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/20 border-t-muted-foreground/50 animate-spin" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/40">
              {t('notificationsView.checking', 'Verificando notificações…')}
            </p>
          </div>
        )}

        {!isLoading && notifications.length === 0 && (
          <EmptyState
            icon={<Bell className="h-8 w-8" />}
            eyebrow={t('notificationsView.title', 'Notificações')}
            title={t('notificationsView.emptyTitle', 'Tudo em dia.')}
            description={t('notificationsView.emptyDescription', 'Nenhuma notificação nova. Você está em dia com tudo.')}
            className="py-20"
          />
        )}

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-6">
          {/* Today */}
          {todayItems.length > 0 && (
            <section>
              <p className="sh-l px-1">
                {t('notificationsView.today', 'Hoje')}
              </p>
              <div>
                {todayItems.map((n, riseIdx) => (
                  <Rise i={riseIdx} key={n.id}>
                    <NotificationRow notification={n} />
                  </Rise>
                ))}
              </div>
            </section>
          )}

          {/* Older */}
          {olderItems.length > 0 && (
            <section>
              <p className="sh-l px-1">
                {t('notificationsView.older', 'Anteriores')}
              </p>
              <div>
                {paginatedOlder.map((n, riseIdx) => (
                  <Rise i={riseIdx} key={n.id}>
                    <NotificationRow notification={n} />
                  </Rise>
                ))}
              </div>

              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-xl border border-border px-5 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
                  >
                    {t('notificationsView.loadMore', 'Carregar mais')}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
